import React, { useLayoutEffect, useRef } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import { HiOutlineUsers, HiOutlineExclamationTriangle } from "react-icons/hi2";
import { capitalizeAllLetters } from "Utils/utils";

export const formatDurationMinutes = (totalMinutes) => {
  if (totalMinutes == null || Number.isNaN(totalMinutes)) return null;
  const absMinutes = Math.abs(Math.round(totalMinutes));
  const h = Math.floor(absMinutes / 60);
  const m = absMinutes % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

export const getDurationBetweenTimes = (startTimeStr, endTimeStr) => {
  if (!startTimeStr || !endTimeStr) return null;
  const start = moment(startTimeStr, "HH:mm");
  let end = moment(endTimeStr, "HH:mm");
  if (!start.isValid() || !end.isValid()) return null;
  if (end.isBefore(start)) end = end.add(1, "day");
  return end.diff(start, "minutes");
};

export const TimesheetDetailCard = ({ children, className = "" }) => (
  <div className={`td-section flex flex-col gap-2.5 ${className}`}>{children}</div>
);

export const TimesheetTabSectionHeader = ({
  title,
  subtitle,
  action,
  className = "",
}) => (
  <div
    className={`flex flex-wrap items-start justify-between gap-3 ${className}`}
  >
    <div className="min-w-0">
      <p className="text-sm font-bold text-[#0F172A]">{title}</p>
      {subtitle && (
        <p className="text-xs font-medium text-[#64748B]">{subtitle}</p>
      )}
    </div>
    {action}
  </div>
);

export const TimesheetWarningBanner = ({
  children,
  variant = "amber",
  className = "",
}) => {
  const styles =
    variant === "red"
      ? "border-[#FECACA] bg-[#FEE2E2] text-[#B91C1C]"
      : "border-[#F5D78E] bg-[#FEF3C7] text-[#92400E]";

  return (
    <div
      className={`mb-2 flex items-center gap-2 rounded-[10px] border px-3 py-2 text-[11px] font-semibold ${styles} ${className}`}
    >
      <HiOutlineExclamationTriangle className="h-4 w-4 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
};

export const TimesheetSharedShiftStrip = ({ title, subtitle, compact }) => (
  <div
    className={`flex items-center gap-2 rounded-lg border border-[#C5D4EA] bg-[#EEF2FB] ${
      compact ? "px-2.5 py-1.5" : "px-3.5 py-2.5"
    }`}
  >
    <HiOutlineUsers
      className={`shrink-0 text-[#4A6BB6] ${compact ? "h-4 w-4" : "h-[18px] w-[18px]"}`}
    />
    <div className="min-w-0">
      <p className="text-[13px] font-bold text-[#0F172A]">{title}</p>
      {subtitle && (
        <p className="text-xs font-medium text-[#64748B]">{subtitle}</p>
      )}
    </div>
  </div>
);

export const TimesheetParticipantRow = ({
  participant,
  participantCount,
  compact = false,
}) => {
  if (!participant) return null;

  const firstName = participant.first_name || "";
  const lastName = participant.last_name || "";
  const fullName = `${capitalizeAllLetters(firstName)} ${capitalizeAllLetters(
    lastName
  )}`.trim();
  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "PT";
  const ndis =
    participant.ndis_number ||
    participant.ndis_no ||
    participant.ndisNo ||
    participant?.participant?.ndis_no ||
    participant?.participant?.ndis_number ||
    null;
  const metaLine = ndis ? `NDIS ${ndis}` : null;

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EEF1F5] text-[11px] font-bold text-[#64748B]"
        >
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold text-[#0F172A]">{fullName}</p>
          {metaLine && (
            <p className="text-[10px] font-medium text-[#64748B]">{metaLine}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2.5 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2.5"
    >
      <div
        className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-[#EEF1F5] text-[13px] font-bold text-[#64748B]"
      >
        {initials}
      </div>
      <div className="min-w-0">
        <p className="text-[13px] font-bold text-[#0F172A]">{fullName}</p>
        {metaLine && (
          <p className="text-[11px] font-medium text-[#64748B]">{metaLine}</p>
        )}
      </div>
    </div>
  );
};

export const TimesheetTimeRow = ({ children }) => (
  <div className="td-time-row flex flex-wrap items-end gap-x-2 gap-y-1">
    {children}
  </div>
);

export const TimesheetTimeToDivider = () => (
  <div className="flex shrink-0 items-center self-end pb-1">
    <span className="text-xs font-medium text-[#94A3B8]">to</span>
  </div>
);

export const TimesheetTimeField = ({ label, children, className = "" }) => (
  <div className={`td-time-field flex flex-col gap-1 min-w-0 ${className}`}>
    {label}
    {children}
  </div>
);

/** Portal calendar — positioned near anchor trigger, avoids panel clipping. */
export const TimesheetInlineDatePicker = ({
  open,
  selected,
  onChange,
  onClose,
  minDate,
  maxDate,
  anchorRef,
}) => {
  const portalRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return;

    const positionPicker = () => {
      const portalEl = portalRef.current;
      if (!portalEl) return;

      const anchorEl = anchorRef?.current;
      const gap = 6;
      const padding = 8;
      const pickerWidth = portalEl.offsetWidth || 280;
      const pickerHeight = portalEl.offsetHeight || 280;

      let top;
      let left;

      if (anchorEl) {
        const anchor = anchorEl.getBoundingClientRect();
        top = anchor.bottom + gap;
        left = anchor.left + anchor.width / 2 - pickerWidth / 2;

        if (top + pickerHeight > window.innerHeight - padding) {
          top = anchor.top - pickerHeight - gap;
        }
      } else {
        top = (window.innerHeight - pickerHeight) / 2;
        left = (window.innerWidth - pickerWidth) / 2;
      }

      left = Math.max(
        padding,
        Math.min(left, window.innerWidth - pickerWidth - padding)
      );
      top = Math.max(
        padding,
        Math.min(top, window.innerHeight - pickerHeight - padding)
      );

      portalEl.style.top = `${top}px`;
      portalEl.style.left = `${left}px`;
    };

    positionPicker();
    requestAnimationFrame(positionPicker);
  }, [open, anchorRef]);

  if (!open) return null;

  return (
    <>
      <div
        className="timesheet-date-picker-backdrop"
        onClick={onClose}
        aria-hidden
      />
      <div ref={portalRef} className="timesheet-date-picker-portal">
        <DatePicker
          inline
          selected={selected}
          onChange={(date) => {
            if (date) onChange(date);
            onClose();
          }}
          minDate={minDate}
          maxDate={maxDate}
          onClickOutside={onClose}
        />
      </div>
    </>
  );
};

export const TimesheetFieldLabel = ({ children, required }) => (
  <label className="td-field-label block">
    {children}
    {required && <span className="text-[#FC466C]"> *</span>}
  </label>
);

export const TimesheetProgressHeader = ({
  title,
  subtitle,
  percent,
  pillLabel,
}) => (
  <div className="space-y-3">
    <div
      className="flex items-center justify-between rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3"
    >
      <div>
        <p className="text-sm font-bold text-[#0F172A]">{title}</p>
        {subtitle && (
          <p className="text-xs font-medium text-[#64748B]">{subtitle}</p>
        )}
      </div>
      {pillLabel && (
        <span
          className="rounded-full bg-[#FEF3C7] px-2.5 py-1 text-xs font-bold text-[#D97706]"
        >
          {pillLabel}
        </span>
      )}
    </div>
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
      <div
        className="h-full rounded-full bg-[#0DBBB3] transition-all duration-300"
        style={{ width: `${percent}%` }}
      />
    </div>
  </div>
);

export const TimesheetStatusChip = ({ children, tone = "neutral" }) => {
  const tones = {
    approve: "bg-[#E2F8F6] text-[#0DBBB3]",
    pending: "bg-[#FEF3C7] text-[#D97706]",
    reject: "bg-[#FFE8EE] text-[#FC466C]",
    brand: "bg-[#EEF2FB] text-[#4A6BB6]",
    neutral: "bg-[#EEF1F5] text-[#64748B]",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${tones[tone] || tones.neutral}`}
    >
      {children}
    </span>
  );
};

export const TimesheetParticipantPill = ({
  label,
  active,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
      active
        ? "border-[#4A6BB6] bg-[#4A6BB6] text-white"
        : "border-[#E5E7EB] bg-[#F8FAFC] text-[#64748B] hover:border-[#C5D4EA] hover:text-[#0F172A]"
    }`}
  >
    {label}
  </button>
);

export const TimesheetMedStatusPill = ({ letter }) => {
  if (letter === "G") {
    return <TimesheetStatusChip tone="approve">Given</TimesheetStatusChip>;
  }
  if (letter === "R") {
    return <TimesheetStatusChip tone="reject">Refused</TimesheetStatusChip>;
  }
  return <TimesheetStatusChip tone="neutral">{letter || "N/A"}</TimesheetStatusChip>;
};
